import { useTheme } from '@crackpoint/shared/components';
import { cn } from '@crackpoint/shared/utils';

interface PuzzleGridProps {
  codeLength: number;
  currentInput: (number | null)[];
  selectedSlot: number | null;
  onSlotSelect: (index: number) => void;
  className?: string;
  disabled?: boolean;
}

export function PuzzleGrid({
  codeLength,
  currentInput,
  selectedSlot,
  onSlotSelect,
  className,
  disabled = false,
}: PuzzleGridProps) {
  const { theme } = useTheme();

  return (
    <div
      className={cn('puzzle-grid', className)}
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${codeLength}, 1fr)`,
        gap: theme.spacing.md,
        justifyContent: 'center',
      }}
    >
      {Array.from({ length: codeLength }).map((_, index) => {
        const isSelected = selectedSlot === index;
        const hasValue = currentInput[index] !== null;

        return (
          <button
            key={index}
            type="button"
            disabled={disabled}
            onClick={() => onSlotSelect(index)}
            className={cn(
              'puzzle-grid__slot',
              isSelected && 'puzzle-grid__slot--selected',
              hasValue && 'puzzle-grid__slot--filled',
            )}
            style={{
              position: 'relative',
              width: '4rem',
              height: '5rem',
              backgroundColor: theme.colours.background.elevated,
              border: `3px solid ${
                isSelected
                  ? theme.colours.accent.primary
                  : hasValue
                    ? theme.colours.accent.secondary
                    : theme.colours.background.tertiary
              }`,
              borderRadius: theme.borders.radius.lg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: disabled ? 'not-allowed' : 'pointer',
              transition: `all ${theme.transitions.normal}`,
              boxShadow: isSelected ? theme.shadows.glow : theme.shadows.sm,
              transform: isSelected ? 'scale(1.05)' : 'scale(1)',
            }}
          >
            <span
              style={{
                fontSize: theme.typography.fontSize.xxl,
                fontWeight: theme.typography.fontWeight.bold,
                color: hasValue ? theme.colours.text.primary : theme.colours.text.tertiary,
                fontFamily: '"JetBrains Mono", monospace',
              }}
            >
              {currentInput[index] !== null ? currentInput[index] : '?'}
            </span>

            {isSelected && (
              <div
                className="puzzle-grid__slot-indicator"
                style={{
                  position: 'absolute',
                  bottom: theme.spacing.xs,
                  width: '6px',
                  height: '6px',
                  backgroundColor: theme.colours.accent.primary,
                  borderRadius: theme.borders.radius.full,
                  animation: 'pulse 1.5s infinite',
                }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

interface PuzzleGridWithFeedbackProps extends PuzzleGridProps {
  feedback?: {
    placed: number;
    misplaced: number;
  } | null;
}

export function PuzzleGridWithFeedback({
  feedback,
  ...gridProps
}: PuzzleGridWithFeedbackProps) {
  const { theme } = useTheme();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <PuzzleGrid {...gridProps} />

      {feedback && (
        <div
          className="puzzle-grid__feedback"
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '2rem',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <div
              style={{
                width: '12px',
                height: '12px',
                backgroundColor: theme.colours.feedback.placed,
                borderRadius: '50%',
              }}
            />
            <span style={{ fontSize: '0.875rem', color: theme.colours.text.secondary }}>
              {feedback.placed} in place
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <div
              style={{
                width: '12px',
                height: '12px',
                border: '2px solid ' + theme.colours.feedback.misplaced,
                borderRadius: '50%',
              }}
            />
            <span style={{ fontSize: '0.875rem', color: theme.colours.text.secondary }}>
              {feedback.misplaced} misplaced
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
